import { useCallback, useMemo, useState } from 'react';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import {
  RequestCareProgramResponse,
  useGetCareProgramRequestsQuery,
} from 'services/careProgramRequestService';
import { CareProgramRequestStatus } from 'features/resources/types';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useCareProgramsData } from './useCareProgramsData';
import { useMemberNotifiedAt } from './useMemberNotifiedAt';
import { isPendingWelcome, isUnseen } from '../utils/careProgramDialogUtils';

export type CareProgramDialogType = 'welcome' | 'declined';

export interface CareProgramDialog {
  type: CareProgramDialogType;
  request: RequestCareProgramResponse;
  programName: string | null;
}

export interface UseCareProgramDialogResult {
  alert: CareProgramDialog | null;
  dismiss: () => void;
  onBrowseCarePrograms: () => void;
}

export const useCareProgramDialog = (): UseCareProgramDialogResult => {
  const user = useLoggedInUser();
  const memberUuid = user?.uuid ?? '';
  const { data: requests, refetch: refetchCareProgramRequest } = useGetCareProgramRequestsQuery(
    { memberUuid },
    { skip: !memberUuid },
  );
  const { careProgramsData } = useCareProgramsData();
  const { markMemberNotified } = useMemberNotifiedAt();
  const navigate = useNavigate();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const pendingRequests = useMemo(
    () => (requests ?? []).filter((request) => isUnseen(request) && !dismissedIds.has(request.id)),
    [requests, dismissedIds],
  );

  const current = useMemo(
    () =>
      pendingRequests.find(isPendingWelcome) ??
      pendingRequests.find((request) => request.status === CareProgramRequestStatus.DECLINED) ??
      null,
    [pendingRequests],
  );

  const alert = useMemo<CareProgramDialog | null>(() => {
    if (!current) return null;

    if (current.status === CareProgramRequestStatus.DECLINED) {
      return { type: 'declined', request: current, programName: null };
    }

    const program = careProgramsData?.find((p) => p.id === current.care_program_id);
    const programName =
      program?.care_program_mappings?.member_facing_name || program?.display_title || null;

    return {
      type: 'welcome',
      request: current,
      programName,
    };
  }, [current, careProgramsData]);

  const dismiss = useCallback(
    (onSuccess?: () => void) => {
      if (!current) return;
      const requestId = current.id;
      setDismissedIds((prev) => new Set(prev).add(requestId));
      markMemberNotified(requestId).finally(() => {
        refetchCareProgramRequest();
        onSuccess?.();
      });
    },
    [current, markMemberNotified, refetchCareProgramRequest],
  );

  const onBrowseCarePrograms = useCallback(() => {
    dismiss(() => navigate('/resources/topics'));
  }, [dismiss, navigate]);

  return { alert, dismiss, onBrowseCarePrograms };
};
